import { useState } from "react";
import { useNavigate } from "react-router";
import { useSnapshot } from "valtio";
import { Mail, ArrowRight, Loader2 } from "lucide-react";
import { db } from "@/lib/instantdb";
import { cn } from "@/lib/utils";
import {
  authStore,
  setMagicLinkSent,
  setLoginError,
  resetAuthStore,
} from "@/lib/stores/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function LoginForm() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showDomainError, setShowDomainError] = useState(false);
  const { magicLinkSent, sentToEmail, loginError } = useSnapshot(authStore);
  const navigate = useNavigate();

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    // Alpha/Beta: Only allow @vizi.video domain
    const emailLower = email.trim().toLowerCase();
    if (!emailLower.endsWith("@vizi.video")) {
      setShowDomainError(true);
      return;
    }

    setIsLoading(true);
    try {
      await db.auth.sendMagicCode({ email: email.trim() });
      setMagicLinkSent(email.trim());
    } catch (err) {
      setLoginError(
        err instanceof Error ? err.message : "Failed to send magic code",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !sentToEmail) return;

    setIsLoading(true);
    try {
      await db.auth.signInWithMagicCode({
        email: sentToEmail,
        code: code.trim(),
      });
      resetAuthStore();
      navigate("/");
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : "Invalid code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    resetAuthStore();
    setCode("");
  };

  return (
    <div className="w-full max-w-sm">
      {!magicLinkSent ? (
        <form onSubmit={handleSendCode} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="pl-11"
                required
                autoComplete="email"
                autoFocus
              />
            </div>
          </div>

          {loginError && (
            <Alert variant="destructive">
              <AlertDescription>{loginError}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            disabled={isLoading || !email.trim()}
            className={cn("w-full", isLoading && "cursor-wait")}
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                Continue with Email
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleVerifyCode} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            We sent a code to{" "}
            <strong className="text-foreground">{sentToEmail}</strong>
          </p>

          <div className="space-y-2">
            <Label htmlFor="code">Verification code</Label>
            <Input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter 6-digit code"
              className="text-center text-lg tracking-widest"
              required
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
            />
          </div>

          {loginError && (
            <Alert variant="destructive">
              <AlertDescription>{loginError}</AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            disabled={isLoading || !code.trim()}
            className={cn("w-full", isLoading && "cursor-wait")}
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                Verify Code
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={handleBack}
            className="w-full"
          >
            Use a different email
          </Button>
        </form>
      )}

      <AlertDialog open={showDomainError} onOpenChange={setShowDomainError}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Access Restricted</AlertDialogTitle>
            <AlertDialogDescription>
              During the alpha and beta period, only email addresses with the
              @vizi.video domain are allowed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
